import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MdRotateLeft } from "react-icons/md";
import { css } from "../../../../styled-system/css";
import { HStack, VStack } from "../../../../styled-system/jsx";
import { Button } from "../../../components/core/basic/Button";
import { RT8ColumnSelector } from "../../../components/core/blocks/MetricSelector";
import { SearchInput } from "../../../components/fields/base/TextField";
import { commaSearchFilterFn, customFilterFn } from "../../../components/table/filterFns";
import { RT8Pagination } from "../../../components/table/Pagination";
import { Table } from "../../../components/table/Table";
import { useClientTable } from "../../../components/table/tableFeatures";
import { usePersistedTableState } from "../../../components/table/usePersistedTableState";
import { resetTable } from "../../../components/table/utils";
import { RT8FilterWidget } from "../../merchandising/components/tables/FilterWidget";
import { amcReportTableSpec, amcReportTableType } from "../utils/utils";

const ReportTable = ({ reportType, spec, reportData }) => {
  const { t } = useTranslation(["amc", "common"]);

  const columns = useMemo(() => spec.columns(t), [spec, t]);
  const globalFilterFn = useMemo(
    () => commaSearchFilterFn((row) => spec.searchFields.map((field) => String(row[field] ?? ""))),
    [spec],
  );
  const data = useMemo(
    () => reportData?.filter((row) => row[spec.requiredField] != null && row[spec.requiredField] !== ""),
    [reportData, spec],
  );

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: `amcReport-${reportType}`,
    initialState: { sorting: spec.defaultSorting },
  });

  const table = useClientTable({
    data,
    columns,
    defaultColumn: { filterFn: customFilterFn },
    globalFilterFn,
    initialState,
    state,
    ...setters,
  });

  return (
    <>
      <HStack padding={"small"} fontSize={"large"}>
        <VStack flex="1" alignItems={"start"}>
          <HStack width={"100%"}>
            <Button
              variant={"contentOnly"}
              aria-label={t(($) => $.common.table["Reset table filters"])}
              onPress={() => resetTable(table)}
              fontSize={"xxlarge"}
            >
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <SearchInput
              aria-label={t(($) => $.common.actions["Search"])}
              isExpandable
              css={{ flex: "0 1 350px" }}
              value={table.state.globalFilter ?? ""}
              onChange={table.setGlobalFilter}
            />
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table table={table} />
    </>
  );
};

export const AMCReportTable = ({ reportData, reportDetails }) => {
  const reportType = amcReportTableType(reportDetails?.query_type);
  const spec = amcReportTableSpec(reportType);

  if (!spec) {
    return null;
  }
  // The persisted state is read once, at mount, and its key and columns differ by report type.
  return <ReportTable key={reportType} reportType={reportType} spec={spec} reportData={reportData} />;
};
